import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Image as ImageIcon, X, Loader2 } from "lucide-react";
import { api, API_URL, formatApiError, resolveMediaUrl } from "@/lib/api";

/**
 * Reusable file upload component.
 * Props:
 *  - value: current URL (string)
 *  - onChange: (url: string) => void
 *  - folder: string for organizing storage
 *  - accept: file input accept attr (default 'image/*')
 *  - label: string
 *  - aspect: 'square' | 'wide' | 'portrait'
 *  - fit: 'contain' | 'cover' — contain untuk logo/favicon agar tidak terdistorsi
 */
const FileUpload = ({ value, onChange, folder = "umum", accept = "image/*", label = "Upload gambar", aspect = "square", fit = "cover", testid }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Ukuran file maksimal 8 MB");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/media/upload`, fd, {
        params: { folder },
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(resolveMediaUrl(data.url));
      toast.success("File berhasil diunggah");
    } catch (e) {
      toast.error(formatApiError(e.response?.data) || "Upload gagal");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const aspectClass = { square: "aspect-square", wide: "aspect-[16/9]", portrait: "aspect-[3/4]" }[aspect] || "aspect-square";
  const fitClass = fit === "contain" ? "object-contain p-3" : "object-cover";

  return (
    <div>
      {label && <label className="block text-sm font-semibold text-zinc-800 mb-2">{label}</label>}
      <div className={`relative ${aspectClass} rounded-2xl border-2 border-dashed border-zinc-200 bg-zinc-50 overflow-hidden group hover:border-maroon-400 transition`}>
        {value ? (
          <>
            <img src={resolveMediaUrl(value)} alt="preview" className={`absolute inset-0 w-full h-full ${fitClass}`} />
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/95 text-red-600 flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition"
              aria-label="Hapus"
            >
              <X size={14} />
            </button>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="absolute bottom-2 right-2 px-3 py-1.5 rounded-full bg-white/95 text-zinc-800 text-xs font-semibold shadow-md opacity-0 group-hover:opacity-100 transition"
            >
              Ganti
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            data-testid={testid}
            className="absolute inset-0 w-full h-full flex flex-col items-center justify-center gap-2 text-zinc-500 hover:text-maroon-700"
          >
            {uploading ? <Loader2 className="animate-spin" size={22} /> : <Upload size={22} />}
            <span className="text-xs font-semibold">{uploading ? "Mengunggah..." : "Klik untuk upload"}</span>
            <span className="text-[10px] text-zinc-400">Max 8 MB • auto-convert WebP</span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
      />
      <div className="mt-2 flex gap-2 items-center">
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="atau tempel URL langsung..."
          className="flex-1 px-3 py-2 rounded-lg border border-zinc-200 text-xs focus:border-maroon-700 outline-none"
        />
      </div>
    </div>
  );
};

export default FileUpload;
